import { yupResolver } from '@hookform/resolvers/yup';
import { Grid } from '@mui/material';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormButton } from 'thermia-react-container';
import { useDeviceFilter } from '../../../../../hooks/useDeviceFilter';
import { Device } from '../../../../../types/device';
import { Registration } from '../../../../../types/registrationTypes';
import RegistrationDevicesYup from '../../../../../validation/yup-input/registrationDevices';
import CompleteRegistrationButton from '../../../components/completeRegistrationButton';
import {NEW_REGISTRATION_ID, StyledContainer} from '../../../registration';
import DeviceSelect from '../components/deviceSelect';
import DeviceTable from '../components/deviceTable';
import {useIsAdmin} from "../../../../../hooks/useIsAdmin";

interface Props {
  registration?: Registration | null;
  unregisteredDevices: Device[] | null;
  updateDevices: (data: Registration) => Promise<Registration>;
  loading: boolean;
  preventSwitchTabDueToUnsavedData: (value: boolean) => void;
  disabled: boolean;
  registrationIsCompleted: boolean;
}

const DevicesForm: FC<Props> = ({
  registration,
  updateDevices,
  loading,
  unregisteredDevices,
  preventSwitchTabDueToUnsavedData,
  disabled,
  registrationIsCompleted
}) => {
  const { t } = useTranslation('registrations');
  const deviceFilter = useDeviceFilter();
  const isAdmin = useIsAdmin();

  // Provide installation date for validation. Do not validate if admin (provide undefined).
  const installationDate = isAdmin ? undefined : registration?.location?.installationDate
  const schema = RegistrationDevicesYup(installationDate);

  const {
    setValue,
    watch,
    trigger,
    reset,
    setError,
    clearErrors,
    formState: { errors, isDirty }
  } = useForm({
    defaultValues: {
      devices: registration?.devices?.items || [],
      deviceSerialNumber: ''
    },
    mode: 'onBlur',
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    reset({
      devices: registration?.devices?.items ?? [],
      deviceSerialNumber: ''
    });
  }, [registration?.devices?.items, reset]);

  const devices = watch('devices');
  const deviceSerialNumber = watch('deviceSerialNumber');
  const normalizeDeviceSerialNumber = deviceSerialNumber.trim().toUpperCase();
  

  const handleSubmit = async () => {
    const deviceListValid = await trigger('devices');
    if (deviceListValid && registration) {
      try {
        const updatedRegistration = await updateDevices({
          ...registration,
          devices: devices.length > 0 ? { items: devices } : undefined
        });

        reset({
          devices: updatedRegistration.devices?.items ?? [],
          deviceSerialNumber
        });
      } catch (error: any) {}
    }
  };

  useEffect(() => {
    preventSwitchTabDueToUnsavedData(isDirty);
  }, [isDirty, preventSwitchTabDueToUnsavedData]);

  const handleAddDevice = async () => {
    const valid = await trigger('deviceSerialNumber', { shouldFocus: true });

    const unDevice = unregisteredDevices?.find((uDevice) => uDevice.serialNumber === normalizeDeviceSerialNumber);

    if (valid && unDevice) {
      setValue('deviceSerialNumber', '', { shouldDirty: false });
      setValue('devices', (devices ?? []).concat(unDevice), {
        shouldDirty: true
      });
    } else {
      setError('deviceSerialNumber', {
        message: `${t('registrations.general.notValid')}`
      });
    }
  };
  const handleOnChange = (value: Device['serialNumber'] | null) => {
    clearErrors('deviceSerialNumber');
    setValue('deviceSerialNumber', value ?? '', { shouldDirty: false });
  };

  const availableDevices = unregisteredDevices?.filter((uDevice) => {
    const deviceIsInList = devices.some((rDevice) => rDevice.serialNumber === uDevice.serialNumber);
    if (deviceIsInList) {
      return false;
    }

    return deviceFilter(uDevice, normalizeDeviceSerialNumber);
  });

  const handleRemoveDeviceFromList = (serialNumber: Device['serialNumber']) => {
    setValue(
      'devices',
      devices.filter((device) => device.serialNumber !== serialNumber),
      { shouldDirty: true }
    );
  };

  return (
    <>
      <StyledContainer>
        <Grid container>
          <Grid item xs={8} container spacing={2} alignItems="center">
            {!disabled && (
              <>
                <Grid item xs={8}>
                  <DeviceSelect
                    disabled={disabled}
                    devices={availableDevices}
                    onChange={handleOnChange}
                    value={deviceSerialNumber}
                    error={errors.deviceSerialNumber}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormButton disabled={disabled} variant={'contained'} title={t('registrations.general.addDevice')} onClick={handleAddDevice} />
                </Grid>
              </>
            )}
            <Grid item xs={12} minWidth={700}>
              <DeviceTable
                registration={registration!}
                label={t('registrations.general.device', { context: 'plural' })}
                handleRemoveDeviceFromList={handleRemoveDeviceFromList}
                devices={devices}
                disabled={disabled}
                registrationIsCompleted={registrationIsCompleted}
              />
            </Grid>
          </Grid>
        </Grid>
      </StyledContainer>
      {!disabled && (
        <Grid container spacing={2} justifyContent={'end'} alignItems={'center'} marginTop={2}>
          <Grid item>
            <FormButton variant={'contained'} onClick={handleSubmit} title={t('registrations.general.save')} loading={loading} />
          </Grid>
          <Grid item>
            <CompleteRegistrationButton formIsDirty={isDirty} />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default DevicesForm;
